import { useState, useEffect, useRef } from 'react';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/useCookieConsent';

interface CookieConsentBannerProps {
  /**
   * Optional delay in ms before showing the banner
   * Useful for showing after other modals close
   */
  delayMs?: number;
  /**
   * If true, completely prevents the banner from rendering
   * Used to block display until country selection is complete
   */
  disabled?: boolean;
}

export default function CookieConsentBanner({ delayMs = 0, disabled = false }: CookieConsentBannerProps) {
  const { showBanner, acceptConsent, declineConsent } = useCookieConsent();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (showBanner) {
      // Apply delay before showing banner
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delayMs);

      return () => clearTimeout(timer);
    }
  }, [showBanner, delayMs]);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch contentJson');
        const data = await response.json();
        const privacyData = data.find((page: any) => page.name === 'Privacy Policy');
        
        if (privacyData?.fields?.[0]?.components?.[0]?.components) {
          const privacyLen = privacyData.fields[0].components[0].components.length;
          for (let i = 0; i < privacyLen; i++) {
            const component = privacyData.fields[0].components[0].components[i];
            if ('key' in component) {
              if (component.key === 'privacyPolicyDocument' && component.defaultValue?.length > 0) {
                setModalContent(component.defaultValue[0].data.text);
                break;
              } else if (component.key === 'privacyPolicyTemplate') {
                setModalContent(component.defaultValue);
              }
            }
          }
        }
      } catch (e) {
        console.error('Error fetching privacy policy:', e);
      }
    };
    
    fetchPrivacyPolicy();
  }, []);

  const handleAccept = () => {
    if (isClosingRef.current) {
      return;
    }
    isClosingRef.current = true;
    setIsAnimatingOut(true);
    setTimeout(() => {
      acceptConsent();
      setIsVisible(false);
      isClosingRef.current = false;
    }, 300);
  };

  const handleDecline = () => {
    if (isClosingRef.current) {
      return;
    }
    isClosingRef.current = true;
    setIsAnimatingOut(true);
    setTimeout(() => {
      declineConsent();
      setIsVisible(false);
      isClosingRef.current = false;
    }, 300);
  };

  const handleShowModal = () => {
    setModalTitle('Privacy Policy');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalTitle('');
  };

  // Don't render if banner shouldn't be shown or if disabled
  if (disabled || !showBanner || !isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay - subtle */}
      <div
        className={`fixed inset-0 bg-black/20 z-[9998] transition-opacity duration-300 ${
          isAnimatingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        onClick={() => !isAnimatingOut && handleDecline()}
      />

      {/* Cookie Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-500 ease-out ${
          isAnimatingOut
            ? 'translate-y-full opacity-0'
            : 'translate-y-0 opacity-100'
        }`}
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent banner"
      >
        <div className="bg-gradient-to-r from-white via-primary-50 to-white border-t-4 border-primary-600 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Icon & Text */}
              <div className="flex items-start gap-3 flex-1">
                {/* <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                    <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div> */}

                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    Cookie Preferences
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                      Required
                    </span>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    This platform uses cookies and third-party tools for analytics, user behavior tracking, and lead management to improve your experience. By clicking 'Accept', you agree to our use of these technologies as outlined in our{' '}
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleShowModal();
                      }} 
                      className="font-semibold text-primary-700 hover:underline cursor-pointer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-shrink-0">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDecline();
                  }}
                  variant="outline"
                  size="lg"
                  className="flex-1 sm:flex-none border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 font-semibold transition-all duration-200"
                >
                  Decline
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccept();
                  }}
                  size="lg"
                  className="flex-1 sm:flex-none btn-primary-gradient hover:btn-primary-gradient text-white font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40"
                >
                  <Cookie className="w-4 h-4 mr-2" />
                  Accept Cookies
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full mx-4 border-t-4 border-emerald-500 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center border-b px-6 py-4 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1 text-gray-800" dangerouslySetInnerHTML={{ __html: modalContent || 'Content not available' }} />
            <div className="flex justify-end px-6 py-4 border-t flex-shrink-0">
              <Button onClick={handleCloseModal} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
